import { tWithKeyAttr as t } from "../../i18nHelper";
import i18n from "../../i18n";
import Loader from "../loader/loader";
import { useGetMyQuestionnaireTasksQuery } from "../../services/api/user.api";
import { IQuestionnaireTask } from "../../services/types/user.types";
import { getPrintDate } from "../../utils/utils";
import styles from "./myQuestionnaires.module.css";
import HotlineQuestionnaireCard from "./hotlineQuestionnaireCard";
import { useGetMyHotlineQuestionnaireQuery } from "../../services/api/user.api";

/**
 * The questionnaires the patient's treatment protocols have asked for.
 *
 * The same rows reach the patient by SMS and by email; this is the third surface, for the patient
 * who never followed a link. Completed ones stay on the list rather than disappearing, so the
 * patient can see what they have already done.
 */

/** Mirrors the server's ProtocolQuestionnaireTriggers. */
const TRIGGER_AFTER_EACH = 1;
const TRIGGER_EVERY_N = 2;
const TRIGGER_BEFORE_FIRST = 3;
const TRIGGER_MID_COURSE = 4;
const TRIGGER_AFTER_LAST = 5;

/** Why this questionnaire was asked for, in the patient's terms. */
function occasionLabel(task: IQuestionnaireTask): string {
  switch (task.triggerType) {
    case TRIGGER_BEFORE_FIRST:
      return t("QUESTIONNAIRE_TASK_BEFORE_FIRST");
    case TRIGGER_MID_COURSE:
      return t("QUESTIONNAIRE_TASK_MID_COURSE");
    case TRIGGER_AFTER_LAST:
      return t("QUESTIONNAIRE_TASK_AFTER_LAST");
    case TRIGGER_AFTER_EACH:
    case TRIGGER_EVERY_N:
    default:
      return task.sessionNumber > 0
        ? t("QUESTIONNAIRE_TASK_AFTER_SESSION", { number: task.sessionNumber })
        : "";
  }
}

function MyQuestionnaires() {
  const { data, isLoading, isError } = useGetMyQuestionnaireTasksQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  // Read here as well as inside the card, so "you have nothing" is not printed underneath a
  // questionnaire the patient plainly does have. RTK Query serves both callers from one request.
  const { data: hotline } = useGetMyHotlineQuestionnaireQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const hasHotlineQuestionnaire = Boolean(hotline?.questionnaire);

  if (isLoading) return <Loader />;

  if (isError) {
    return (
      <section dir={i18n.dir()} className={styles.section}>
        <h3 className={styles.section__title}>{t("QUESTIONNAIRE_TASKS_TITLE")}</h3>
        <HotlineQuestionnaireCard />
        <p className={styles.error}>{t("QUESTIONNAIRE_TASK_LOAD_ERROR")}</p>
      </section>
    );
  }

  const tasks = data?.tasks ?? [];
  const openCount = data?.openCount ?? 0;

  return (
    <section dir={i18n.dir()} className={styles.section}>
      <h3 className={styles.section__title}>{t("QUESTIONNAIRE_TASKS_TITLE")}</h3>

      {/* Above the protocol tasks: this is the one the patient was texted about, and it has an
          expiry the others do not. Renders nothing when nothing is owed. */}
      <HotlineQuestionnaireCard />

      {tasks.length === 0 ? (
        hasHotlineQuestionnaire ? null : (
          <p className={styles.empty}>{t("QUESTIONNAIRE_TASKS_EMPTY")}</p>
        )
      ) : (
        <>
          {openCount > 0 && (
            <p className={styles.openCount}>
              {t("QUESTIONNAIRE_TASKS_OPEN_COUNT", { count: openCount })}
            </p>
          )}

          <ul className={styles.list}>
            {tasks.map((task) => {
              const occasion = occasionLabel(task);

              return (
                <li
                  key={task.id}
                  className={`${styles.item} ${task.isCompleted ? styles.item_done : ""}`}
                >
                  <div className={styles.itemBody}>
                    <span className={styles.name}>
                      {task.questionnaireName || task.protocolName || ""}
                    </span>
                    <span className={styles.meta}>
                      {occasion && <span>{occasion}</span>}
                      {task.appointmentDate && (
                        <span>{getPrintDate(new Date(task.appointmentDate))}</span>
                      )}
                      {task.programName && <span>{task.programName}</span>}
                    </span>
                  </div>

                  <span
                    className={`${styles.status} ${
                      task.isCompleted ? styles.status_done : styles.status_pending
                    }`}
                  >
                    {task.isCompleted
                      ? t("QUESTIONNAIRE_TASK_DONE")
                      : t("QUESTIONNAIRE_TASK_PENDING")}
                  </span>

                  {/* The very link the SMS and the email carry — built once on the server, so
                      the tab can never send the patient somewhere else. */}
                  {!task.isCompleted && task.link && (
                    <a
                      className={styles.fillBtn}
                      href={task.link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("QUESTIONNAIRE_TASK_FILL")}
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}

export default MyQuestionnaires;
